import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Settings, Trophy } from 'lucide-react';
import LeagueSettingsOverlay from './LeagueSettingsOverlay';
import { AggregatePeriod, useAggregateLeaderboard } from '@/api/query/useLeaderboard';
import { useUser } from '@/hooks/useUser';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { AddUserIcon } from '@/components/icons/AddUserIcon';
import { useLeagueDetails } from '@/hooks/useLeagueDetails';
import { WeekFilter } from './WeekFilter';
import { useWeeks } from './WeekFilter/useWeeks';
import { ShareLeagueCodeModal } from '../modals/ShareLeagueCodeModal';
import { useAuth } from '@/contexts/AuthContext';

interface LeagueLeaderboardViewProps {
  leagueId: string;
  leagueColor: 'orange' | 'blue';
  onBack: () => void;
}

const LeagueLeaderboardView: React.FC<LeagueLeaderboardViewProps> = ({
  leagueId,
  leagueColor: _leagueColor,
  onBack,
}) => {
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState<AggregatePeriod>('week');
  const [showSettings, setShowSettings] = useState(false);
  const [showLeagueCreated, setShowLeagueCreated] = useState(false);

  const { iframeParams } = useAuth();
  const { weeks, latestWeekOffset, currentISOWeek } = useWeeks();
  // selectedWeek is a *week index* (0-based), representing Week (index+1)
  const [selectedWeek, setSelectedWeek] = useState(latestWeekOffset);
  const now = useMemo(() => new Date(), []);
  const [selectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear] = useState(now.getFullYear());

  const leagueDetails = useLeagueDetails(leagueId);
  const league = leagueDetails?.league;
  const leagueName = league?.name ?? 'League';

  const { currentUser } = useUser();
  const currentUserId = currentUser?.id;

  const leagueCode = iframeParams.location ?? 'ca';
  const weekOffset = useMemo(() => {
    const selectedWeekNumber = selectedWeek + 1;
    return currentISOWeek - selectedWeekNumber;
  }, [selectedWeek, currentISOWeek]);

  const leaderboardParams = useMemo(() => {
    if (activeTab === 'week') {
      return { period: 'week', weekOffset, leagueCode, friendsLeagueId: leagueId } as const;
    }
    if (activeTab === 'month') {
      return {
        period: 'month',
        month: selectedMonth,
        year: selectedYear,
        leagueCode,
        friendsLeagueId: leagueId,
      } as const;
    }
    return { period: 'year', leagueCode, friendsLeagueId: leagueId } as const;
  }, [activeTab, weekOffset, leagueCode, leagueId, selectedMonth, selectedYear]);

  const leaderboardQuery = useAggregateLeaderboard(leaderboardParams);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const settingsParam = params.get('show');
    if (settingsParam === 'code') {
      setShowLeagueCreated(true);
    }
    if (settingsParam === 'settings') {
      setShowSettings(true);
    }
  }, [search]);

  const tabs: { id: AggregatePeriod; label: string }[] = [
    { id: 'week', label: 'Week' },
    { id: 'month', label: 'Month' },
    { id: 'year', label: 'Year' },
  ];

  const leaderboardEntries =
    leaderboardQuery.data?.pages.flatMap(page => page.leaderboard ?? []) ?? [];
  const isLoadingLeaderboard = leagueDetails?.isLoading || leaderboardQuery.isLoading;

  const onAddUser = () => setShowLeagueCreated(true);

  const handleTabChange = (next: AggregatePeriod) => {
    setActiveTab(next);

    if (next === 'week') {
      setSelectedWeek(latestWeekOffset);
    }
  };

  const weekClickHandler = (weekIndex: number) => {
    setSelectedWeek(weekIndex);
  };

  return (
    <div data-test-id="league-leaderboard-view" className="h-full flex flex-col relative">
      <header className="bg-app-background border-b border-border/30 p-4">
        <div className="flex items-center justify-between">
          <button
            data-test-id="back-to-leagues-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-text-primary hover:text-text-secondary transition-smooth"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>

          {league?.isOwner && (
            <button
              data-test-id="open-league-settings-btn"
              onClick={() => setShowSettings(true)}
              className="p-2 text-text-primary hover:text-text-secondary transition-smooth"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
        </div>
      </header>

      <div className="px-4 py-4 flex items-center justify-center gap-2 text-center border-b border-border/30 relative">
        {league?.isOwner && (
          <button aria-label="Add user" onClick={onAddUser} className="absolute left-3">
            <AddUserIcon className="w-6 h-6" />
          </button>
        )}

        <h1 className="text-text-primary font-[800] text-xl uppercase">{leagueName}</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4">
          <div className="flex space-x-1 bg-game-card rounded-xl">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-test-id={`period-tab-${tab.id}`}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-smooth
                  ${
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-white hover:text-text-primary'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4">
            {/* Week Selection */}
            {activeTab === 'week' && (
              <WeekFilter
                weeks={weeks}
                selectedWeek={selectedWeek}
                setSelectedWeek={weekClickHandler}
              />
            )}
          </div>
        </div>

        <div className="px-4 pb-2">
          <div className="flex items-center justify-between text-text-secondary text-sm font-medium">
            <div className="flex items-center gap-4">
              <span>Rank</span>
              <span>User</span>
            </div>
            <span>Points</span>
          </div>
        </div>

        <div className="px-4 pb-6">
          {isLoadingLeaderboard && <LoadingSpinner message="Loading leaderboard..." />}

          {!isLoadingLeaderboard && leaderboardEntries.length === 0 && (
            <div
              data-test-id="league-leaderboard-empty-state"
              className="bg-game-card rounded-xl p-6 text-center"
            >
              <p className="text-text-secondary">
                No scores yet for this {activeTab}. Check back once picks are scored.
              </p>
            </div>
          )}

          {!isLoadingLeaderboard && leaderboardEntries.length > 0 && (
            <div className="space-y-2">
              {leaderboardEntries.map(player => {
                const isCurrentUser = !!currentUserId && player.user_id === currentUserId;
                const isWinner = player.rank === 1;

                return (
                  <div
                    key={`${player.rank}-${player.user_id}`}
                    data-test-id={`league-leaderboard-row-${player.rank}`}
                    data-test-state={isCurrentUser ? 'current-user' : 'other-user'}
                    className={`
                      rounded-xl p-4 border-0 overflow-hidden
                      ${isCurrentUser ? 'bg-bet99-primary text-white' : 'bg-game-card'}
                    `}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-4">
                        <span className="font-bold text-lg">{player.rank}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{player.username ?? 'Unknown'}</span>
                          {isWinner && (
                            <Trophy
                              data-test-id={`trophy-icon-${player.rank}`}
                              className="w-4 h-4 text-yellow-400"
                            />
                          )}
                        </div>
                      </div>
                      <span className="font-bold text-lg">{player.total_points}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {league && (
        <LeagueSettingsOverlay
          isOpen={showSettings && league?.isOwner}
          onClose={() => setShowSettings(false)}
          league={league}
          users={league?.members || []}
        />
      )}

      {league && (
        <ShareLeagueCodeModal
          isOpen={showLeagueCreated}
          onClose={() => setShowLeagueCreated(false)}
          league={league}
          onViewLeague={() => setShowLeagueCreated(false)}
        />
      )}
    </div>
  );
};

export default LeagueLeaderboardView;
